import React, { useEffect } from "react";
import { Input, Label, Form, FormFeedback, Button, Spinner } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";

import { registerUser, apiError, resetRegisterFlag } from "../../slices/thunks";
import AhAuthShell from "./AhAuthShell";
import GoogleSignInButton from "./GoogleSignInButton";

const Register = () => {
    const dispatch = useDispatch();

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            confirm_password: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Enter a valid email").required("Please enter your email"),
            password: Yup.string().min(6, "At least 6 characters").required("Please enter your password"),
            first_name: Yup.string().required("Please enter your first name"),
            last_name: Yup.string().required("Please enter your last name"),
            confirm_password: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords do not match")
                .required("Please confirm your password"),
        }),
        onSubmit: (values) => {
            dispatch(registerUser(values));
        }
    });

    const selectLayoutState = (state) => state.Account;
    const registerdatatype = createSelector(
        selectLayoutState,
        (account) => ({
            success: account.success,
            error: account.error,
            loading: account.loading,
        })
    );
    const { error, success, loading } = useSelector(registerdatatype);

    useEffect(() => {
        dispatch(apiError(""));
        dispatch(resetRegisterFlag());
    }, [dispatch]);

    document.title = "Create account | ApiHub";

    return (
        <AhAuthShell
            eyebrow="GET STARTED"
            title="Create your ApiHub account"
            subtitle="One key, every AI service. Free playground included."
            footer={<>Already have an account? <Link to="/login">Sign in</Link></>}
            meta={
                <>
                    <span className="ah-check">No credit card</span>
                    <span className="ah-check">Free playground</span>
                    <span className="ah-check">Cancel anytime</span>
                </>
            }
        >
            {success && (
                <div className="ah-notice" role="alert">
                    <div className="ah-notice-icon">
                        <i className="ri-mail-send-line"></i>
                    </div>
                    <div className="ah-notice-body">
                        <strong>Account created — check your inbox.</strong>
                        <div style={{ marginTop: 4, color: "oklch(0.82 0.06 65)" }}>
                            We sent a verification link to <strong style={{ color: "var(--ah-fg)" }}>{validation.values.email}</strong>. Click it to activate your account, then come back to sign in.
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Link to="/login" className="btn btn-primary btn-sm">Go to sign in →</Link>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: 20 }}>
                    {typeof error === "string" ? error : (error && error.message) || "Failed to register. Please try again."}
                </div>
            )}

            <div style={{ marginBottom: 16 }}>
                <GoogleSignInButton label="Sign up with Google" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 16px", color: "var(--ah-fg-dim)", fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: "var(--ah-border)" }} />
                <span>or sign up with email</span>
                <div style={{ flex: 1, height: 1, background: "var(--ah-border)" }} />
            </div>

            <Form
                onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}
                className="needs-validation"
                action="#"
            >
                <div className="mb-3">
                    <Label htmlFor="email" className="form-label">Email</Label>
                    <Input
                        id="email"
                        name="email"
                        placeholder="you@example.com"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={validation.touched.email && !!validation.errors.email}
                    />
                    {validation.touched.email && validation.errors.email && (
                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    )}
                </div>

                <div className="row">
                    <div className="mb-3 col-6">
                        <Label htmlFor="first_name" className="form-label">First name</Label>
                        <Input
                            id="first_name"
                            name="first_name"
                            type="text"
                            placeholder="Jane"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.first_name || ""}
                            invalid={validation.touched.first_name && !!validation.errors.first_name}
                        />
                        {validation.touched.first_name && validation.errors.first_name && (
                            <FormFeedback type="invalid">{validation.errors.first_name}</FormFeedback>
                        )}
                    </div>
                    <div className="mb-3 col-6">
                        <Label htmlFor="last_name" className="form-label">Last name</Label>
                        <Input
                            id="last_name"
                            name="last_name"
                            type="text"
                            placeholder="Doe"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.last_name || ""}
                            invalid={validation.touched.last_name && !!validation.errors.last_name}
                        />
                        {validation.touched.last_name && validation.errors.last_name && (
                            <FormFeedback type="invalid">{validation.errors.last_name}</FormFeedback>
                        )}
                    </div>
                </div>

                <div className="mb-3">
                    <Label htmlFor="password" className="form-label">Password</Label>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="At least 6 characters"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.password || ""}
                        invalid={validation.touched.password && !!validation.errors.password}
                    />
                    {validation.touched.password && validation.errors.password && (
                        <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                    )}
                </div>

                <div className="mb-3">
                    <Label htmlFor="confirm_password" className="form-label">Confirm password</Label>
                    <Input
                        id="confirm_password"
                        name="confirm_password"
                        type="password"
                        placeholder="Re-enter your password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.confirm_password || ""}
                        invalid={validation.touched.confirm_password && !!validation.errors.confirm_password}
                    />
                    {validation.touched.confirm_password && validation.errors.confirm_password && (
                        <FormFeedback type="invalid">{validation.errors.confirm_password}</FormFeedback>
                    )}
                </div>

                <p style={{ fontSize: 12, color: "var(--ah-fg-dim)", margin: "8px 0 16px" }}>
                    By creating an account you agree to the ApiHub <Link to="#" style={{ color: "var(--ah-fg-muted)", borderBottom: "1px solid var(--ah-accent)" }}>Terms of Use</Link>.
                </p>

                <Button color="primary" className="w-100" type="submit" disabled={!!loading}>
                    {loading ? <><Spinner size="sm" className="me-2" />Creating…</> : "Create account →"}
                </Button>
            </Form>
        </AhAuthShell>
    );
};

export default Register;
